import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { M14_ROLES, hasAnyRole } from '../../lib/roles';
import type { LicenceApplication, RequirementResult } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { ErrorNote, Field, InfoNote, PageHeader, Spinner, StatusBadge } from '../../ui/components';

export function LicenceApplicationDetail() {
  const { id = '' } = useParams();
  const { persona } = useAuth();
  const isLicensingOfficer = hasAnyRole(persona?.roles, M14_ROLES.LICENSING_OFFICER, M14_ROLES.SENIOR_LO);
  const isBoardSecretariat = hasAnyRole(persona?.roles, M14_ROLES.BOARD_SECRETARIAT);

  const appQ = useApiQuery<LicenceApplication>(`/api/v1/m14/applications/${id}`);

  if (appQ.loading) return <Spinner label="Loading application…" />;
  if (appQ.error || !appQ.data) {
    return (
      <div>
        <Link to="/licensing/applications" className="text-sm font-semibold text-brand-600 hover:underline">← Back to applications</Link>
        <div className="mt-4"><ErrorNote message={appQ.error ?? 'Application not found'} /></div>
      </div>
    );
  }
  const app = appQ.data;

  return (
    <div>
      <Link to="/licensing/applications" className="mb-3 inline-block text-sm font-semibold text-brand-600 hover:underline">← Back to applications</Link>
      <PageHeader title={app.classCode.replaceAll('_', ' ')} subtitle={`${app.applicantType} ${app.applicantId}`} actions={<StatusBadge status={app.state} />} />

      {app.rejectionReason ? <div className="mb-4"><ErrorNote message={app.rejectionReason} /></div> : null}
      {app.premisesAddress ? <p className="mb-4 text-sm text-slate-600">Premises: {app.premisesAddress}{app.premisesDistrict ? ` (${app.premisesDistrict})` : ''}</p> : null}

      <div className="card p-5">
        <h2 className="mb-3 text-sm font-bold text-slate-700">Requirement checklist</h2>
        <ul className="space-y-2">
          {(app.requirementResults ?? []).map((r) => (
            <RequirementRow
              key={r.id}
              applicationId={app.id}
              applicantType={app.applicantType}
              applicantId={app.applicantId}
              req={r}
              isLicensingOfficer={isLicensingOfficer}
              onChanged={appQ.reload}
            />
          ))}
        </ul>
      </div>

      {app.state === 'BOARD_AGENDA' && isBoardSecretariat ? <BoardDecision applicationId={app.id} onDecided={appQ.reload} /> : null}
      {app.boardDecisionReason ? (
        <div className="card mt-5 p-4 text-sm"><span className="font-semibold">Board decision:</span> {app.boardOutcome} — {app.boardDecisionReason}</div>
      ) : null}
    </div>
  );
}

function RequirementRow({ applicationId, applicantType, applicantId, req, isLicensingOfficer, onChanged }: {
  applicationId: string; applicantType: 'PERSON' | 'ENTITY'; applicantId: string; req: RequirementResult; isLicensingOfficer: boolean; onChanged: () => void;
}) {
  const { persona } = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showUpload, setShowUpload] = useState(false);
  const [docRef, setDocRef] = useState('');
  const [sha256, setSha256] = useState('');
  const [showInspection, setShowInspection] = useState(false);

  const isSelfApplicant = applicantType === 'ENTITY' ? persona?.actingForBusinessId === applicantId : persona?.nin === applicantId;
  const canUpload = req.type === 'DOC_UPLOAD' && (isSelfApplicant || isLicensingOfficer);
  const actingFor = applicantType === 'ENTITY' ? persona?.actingForBusinessId : undefined;

  const attest = async (approve: boolean) => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m14/applications/${applicationId}/requirements/${req.code}/attest`, { approve });
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Attestation failed');
    } finally {
      setBusy(false);
    }
  };

  const upload = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(
        `/api/v1/m14/applications/${applicationId}/requirements/${req.code}/upload`,
        { evidenceDocRef: docRef, evidenceSha256: sha256 },
        { actingFor },
      );
      setShowUpload(false);
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Upload failed');
    } finally {
      setBusy(false);
    }
  };

  const recordInspection = async (result: 'PASS' | 'FAIL') => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m14/applications/${applicationId}/requirements/${req.code}/inspection-result`, { result, failReasons: [] });
      setShowInspection(false);
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not record inspection result');
    } finally {
      setBusy(false);
    }
  };

  return (
    <li className="rounded-lg border border-slate-200 p-3">
      <div className="flex items-center justify-between">
        <div>
          <span className="text-sm font-medium">{req.label}</span>
          <span className="ml-2 text-[10px] uppercase text-slate-400">{req.type}</span>
        </div>
        <StatusBadge status={req.status} />
      </div>
      {req.note ? <div className="mt-1 text-xs text-slate-500">{req.note}</div> : null}
      {error ? <div className="mt-2"><ErrorNote message={error} /></div> : null}
      {req.status === 'PENDING' ? (
        <div className="mt-2">
          {req.type === 'SEYX_CHECK' && isLicensingOfficer ? (
            <div className="flex gap-1.5">
              <button className="btn-success !px-2 !py-0.5 text-[11px]" onClick={() => attest(true)} disabled={busy}>Attest MET</button>
              <button className="btn-danger !px-2 !py-0.5 text-[11px]" onClick={() => attest(false)} disabled={busy}>Attest FAILED</button>
            </div>
          ) : null}
          {req.type === 'DOC_UPLOAD' && canUpload ? (
            showUpload ? (
              <div className="flex gap-2">
                <input className="field-input" placeholder="Doc ref" value={docRef} onChange={(e) => setDocRef(e.target.value)} />
                <input className="field-input font-mono text-xs" placeholder="sha256 (64 hex)" value={sha256} onChange={(e) => setSha256(e.target.value)} />
                <button className="btn-primary shrink-0 !py-1 text-xs" onClick={upload} disabled={busy || !docRef || sha256.length !== 64}>Upload</button>
              </div>
            ) : (
              <button className="btn-secondary !px-2.5 !py-1 text-xs" onClick={() => setShowUpload(true)}>Upload evidence</button>
            )
          ) : null}
          {req.type === 'INSPECTION' && isLicensingOfficer ? (
            showInspection ? (
              <div className="flex gap-1.5">
                <button className="btn-success !px-2 !py-0.5 text-[11px]" onClick={() => recordInspection('PASS')} disabled={busy}>Passed</button>
                <button className="btn-danger !px-2 !py-0.5 text-[11px]" onClick={() => recordInspection('FAIL')} disabled={busy}>Failed</button>
              </div>
            ) : (
              <button className="btn-secondary !px-2.5 !py-1 text-xs" onClick={() => setShowInspection(true)}>Record inspection result</button>
            )
          ) : null}
          {req.type === 'COMMITTEE' ? <span className="text-xs text-amber-600">Resolved only by the licensing board's decision below.</span> : null}
          {!isLicensingOfficer && (req.type === 'SEYX_CHECK' || req.type === 'INSPECTION') ? (
            <InfoNote message="Awaiting a Licensing Officer's action." />
          ) : null}
        </div>
      ) : null}
    </li>
  );
}

function BoardDecision({ applicationId, onDecided }: { applicationId: string; onDecided: () => void }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const decide = async (outcome: 'APPROVED' | 'REJECTED' | 'DEFERRED') => {
    setBusy(outcome);
    setError(null);
    try {
      await api.post(`/api/v1/m14/applications/${applicationId}/board/decide`, { outcome, reason });
      onDecided();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Decision failed');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="card mt-5 p-5">
      <h2 className="mb-1 text-sm font-bold text-slate-700">Board decision</h2>
      <p className="mb-3 text-xs text-slate-500">This licence class requires the board (liquor, gambling).</p>
      {error ? <div className="mb-3"><ErrorNote message={error} /></div> : null}
      <div className="space-y-2">
        <Field label="Reason / minutes summary"><input className="field-input" value={reason} onChange={(e) => setReason(e.target.value)} /></Field>
        <div className="flex flex-wrap gap-1.5">
          <button className="btn-success !px-2.5 !py-1 text-xs" onClick={() => decide('APPROVED')} disabled={busy !== null || reason.length < 3}>Approve</button>
          <button className="btn-danger !px-2.5 !py-1 text-xs" onClick={() => decide('REJECTED')} disabled={busy !== null || reason.length < 3}>Reject</button>
          <button className="btn-secondary !px-2.5 !py-1 text-xs" onClick={() => decide('DEFERRED')} disabled={busy !== null || reason.length < 3}>Defer</button>
        </div>
      </div>
    </div>
  );
}
